import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import {
  Psychology,
  Assessment,
  Warning,
  Refresh,
  Download,
  Security,
  CalendarToday,
  DocumentScanner,
  AutoAwesome,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  regulatoryIntelligenceService,
  RegulatoryIntelligence,
  PredictiveCompliance,
  RegulatoryCalendarEvent,
  DocumentProcessingResult,
} from '../services/regulatoryIntelligenceService';
import { LoggerService } from '../services/LoggerService';
import { ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const RegulatoryIntelligencePage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Data states
  const [_analyses, setAnalyses] = useState<RegulatoryIntelligence[]>([]);
  const [predictiveCompliance, setPredictiveCompliance] = useState<PredictiveCompliance[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<RegulatoryCalendarEvent[]>([]);
  const [documentProcessing, setDocumentProcessing] = useState<DocumentProcessingResult[]>([]);
  
  // Filter states
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [dateRange, setDateRange] = useState<string>('30_days');
  const [riskLevel, setRiskLevel] = useState<string>('');
  const [jurisdiction, setJurisdiction] = useState<string>('');
  
  // Dialog states
  const [detailDialog, setDetailDialog] = useState<{ open: boolean; data: any; type: string }>({
    open: false,
    data: null,
    type: ''
  });
  const [analysisDialog, setAnalysisDialog] = useState<{ open: boolean; document: any }>({
    open: false,
    document: null
  });

  useEffect(() => {
    loadRegulatoryIntelligence();
  }, [selectedFund, dateRange, riskLevel, jurisdiction]);

  const loadRegulatoryIntelligence = async () => {
    try {
      setLoading(true);
      
      const startDate = getDateRangeStart(dateRange);
      const endDate = new Date().toISOString().split('T')[0];

      const [
        complianceRes,
        calendarRes,
        processingRes
      ] = await Promise.all([
        selectedFund ? regulatoryIntelligenceService.getPredictiveCompliance(selectedFund) : Promise.resolve(getMockPredictiveCompliance()),
        regulatoryIntelligenceService.getRegulatoryCalendar(startDate, endDate, {
          jurisdiction: jurisdiction ? [jurisdiction] : undefined,
        }),
        Promise.resolve(getMockDocumentProcessing())
      ]);

      if (selectedFund) {
        setPredictiveCompliance([complianceRes]);
      } else {
        setPredictiveCompliance(getMockMultipleFundCompliance());
      }
      setCalendarEvents(calendarRes);
      setDocumentProcessing(processingRes);

    } catch (error: any) {
      LoggerService.error('Error loading regulatory intelligence:', error);
      enqueueSnackbar('Failed to load regulatory intelligence data', { variant: 'error' });
      
      // Don't use mock data - show error to user
      setPredictiveCompliance([]);
      setCalendarEvents([]);
      setDocumentProcessing([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeDocument = async (documentId: string, analysisType: string) => {
    try {
      setAnalyzing(true);
      const analysis = await regulatoryIntelligenceService.analyzeRegulatoryDocument(
        documentId,
        analysisType as any,
        {
          jurisdiction: jurisdiction || 'Luxembourg',
          fundTypes: selectedFund ? ['UCITS'] : undefined
        }
      );
      
      setAnalyses(prev => [analysis, ...prev]);
      enqueueSnackbar('Document analysis completed successfully', { variant: 'success' });
      setAnalysisDialog({ open: false, document: null });
    } catch (error: any) {
      LoggerService.error('Error analyzing document:', error);
      enqueueSnackbar('Failed to analyze document', { variant: 'error' });
    } finally {
      setAnalyzing(false);
    }
  };

  const getDateRangeStart = (range: string): string => {
    const now = new Date();
    const days = range === '7_days' ? 7 : range === '30_days' ? 30 : 90;
    const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return startDate.toISOString().split('T')[0];
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // Removed unused function _getRiskLevelColor

  const getComplianceScoreColor = (score: number) => {
    if (score >= 90) return '#4caf50';
    if (score >= 75) return '#ff9800';
    if (score >= 60) return '#ff5722';
    return '#f44336';
  };

  // Mock data generators
  const getMockPredictiveCompliance = () => ({
    fund_id: 'fund-123',
    compliance_score: 78,
    risk_level: 'medium' as const,
    predicted_issues: [
      {
        id: 'issue-1',
        category: 'ESG Reporting',
        severity: 'medium' as const,
        probability: 0.65,
        title: 'ESG Data Quality Issues',
        description: 'Potential data quality issues in ESG reporting due to incomplete third-party data',
        potential_impact: 'Regulatory scrutiny and potential fines',
        early_warning_indicators: ['Data completeness below 90%', 'Third-party data delays'],
        prevention_strategies: ['Implement automated data validation', 'Diversify data provider sources']
      }
    ],
    recommendations: [
      {
        id: 'rec-1',
        priority: 'high' as const,
        category: 'system_upgrade' as const,
        title: 'ESG Data Management Platform',
        description: 'Implement dedicated ESG data management and reporting platform',
        expected_benefit: 'Improved data quality and automated compliance reporting',
        implementation_cost: 125000,
        implementation_timeline: '6 months',
        success_metrics: ['Data completeness > 95%', 'Report generation time < 2 hours']
      }
    ],
    next_assessment_date: '2024-04-15',
    trends: [
      {
        metric: 'Overall Compliance Score',
        direction: 'improving' as const,
        change_percentage: 12,
        time_period: 'Last 6 months',
        contributing_factors: ['Improved documentation processes', 'Staff training completion']
      }
    ]
  });

  const getMockMultipleFundCompliance = () => [
    {
      fund_id: 'fund-123',
      compliance_score: 78,
      risk_level: 'medium' as const,
      predicted_issues: [],
      recommendations: [],
      next_assessment_date: '2024-04-15',
      trends: []
    },
    {
      fund_id: 'fund-456',
      compliance_score: 85,
      risk_level: 'low' as const,
      predicted_issues: [],
      recommendations: [],
      next_assessment_date: '2024-04-20',
      trends: []
    },
    {
      fund_id: 'fund-789',
      compliance_score: 65,
      risk_level: 'high' as const,
      predicted_issues: [],
      recommendations: [],
      next_assessment_date: '2024-04-10',
      trends: []
    }
  ];

  const _getMockCalendarEvents = (): RegulatoryCalendarEvent[] => [
    {
      id: 'event-1',
      title: 'SFDR Periodic Disclosure Deadline',
      description: 'Annual SFDR periodic reports must be published for Article 8 and 9 funds',
      event_type: 'deadline',
      date: '2024-04-30',
      jurisdiction: 'EU',
      regulatory_body: 'ESMA',
      affected_regulations: ['SFDR', 'Taxonomy Regulation'],
      fund_types_affected: ['UCITS', 'AIF'],
      priority: 'critical',
      preparation_recommendations: [],
      ai_generated: true,
      confidence_score: 0.91,
      source_document: 'ESMA-Guidance-2024-03'
    },
    {
      id: 'event-2',
      title: 'CSSF Consultation Period Ends',
      description: 'Consultation on updated UCITS management company regulations',
      event_type: 'consultation',
      date: '2024-05-15',
      jurisdiction: 'Luxembourg',
      regulatory_body: 'CSSF',
      affected_regulations: ['UCITS Directive'],
      fund_types_affected: ['UCITS'],
      priority: 'medium',
      preparation_recommendations: [],
      ai_generated: true,
      confidence_score: 0.88
    }
  ];

  const getMockDocumentProcessing = (): DocumentProcessingResult[] => [
    {
      document_id: 'doc-1',
      processing_status: 'completed',
      extracted_entities: [],
      key_sections: [],
      regulatory_references: [],
      ai_summary: 'New CSSF circular on liquidity risk management for UCITS funds',
      confidence_score: 0.89,
      processing_time_ms: 3200
    }
  ];

  const complianceScoreData = predictiveCompliance.map((pc, index) => ({
    name: `Fund ${index + 1}`,
    score: pc.compliance_score,
    risk: pc.risk_level
  }));

  const riskDistributionData = [
    { name: 'Low Risk', value: predictiveCompliance.filter(pc => pc.risk_level === 'low').length, color: '#4caf50' },
    { name: 'Medium Risk', value: predictiveCompliance.filter(pc => pc.risk_level === 'medium').length, color: '#ff9800' },
    { name: 'High Risk', value: predictiveCompliance.filter(pc => pc.risk_level === 'high').length, color: '#f44336' },
    { name: 'Critical Risk', value: predictiveCompliance.filter(pc => pc.risk_level === 'critical').length, color: '#9c27b0' }
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading Regulatory Intelligence...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Psychology sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          AI Regulatory Intelligence
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Fund</InputLabel>
              <Select
                value={selectedFund}
                label="Fund"
                onChange={(e) => setSelectedFund(e.target.value)}
              >
                <MenuItem value="">All Funds</MenuItem>
                <MenuItem value="fund-123">Global Equity Fund</MenuItem>
                <MenuItem value="fund-456">European Bond Fund</MenuItem>
                <MenuItem value="fund-789">Emerging Markets Fund</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select
                value={dateRange}
                label="Time Period"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="7_days">7 Days</MenuItem>
                <MenuItem value="30_days">30 Days</MenuItem>
                <MenuItem value="90_days">90 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Jurisdiction</InputLabel>
              <Select
                value={jurisdiction}
                label="Jurisdiction"
                onChange={(e) => setJurisdiction(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="EU">European Union</MenuItem>
                <MenuItem value="Luxembourg">Luxembourg</MenuItem>
                <MenuItem value="Ireland">Ireland</MenuItem>
                <MenuItem value="US">United States</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Risk Level</InputLabel>
              <Select
                value={riskLevel}
                label="Risk Level"
                onChange={(e) => setRiskLevel(e.target.value)}
              >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={loadRegulatoryIntelligence}
              disabled={loading}
            >
              Refresh Data
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" icon={<Assessment />} />
          <Tab label="Predictive Compliance" icon={<AutoAwesome />} />
          <Tab label="Document Analysis" icon={<DocumentScanner />} />
          <Tab label="Regulatory Calendar" icon={<CalendarToday />} />
          <Tab label="Risk Analytics" icon={<Security />} />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Box>
          {/* Key Metrics Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Avg Compliance Score
                      </Typography>
                      <Typography variant="h4" style={{ 
                        color: getComplianceScoreColor(
                          predictiveCompliance.reduce((sum, pc) => sum + pc.compliance_score, 0) / predictiveCompliance.length
                        ) 
                      }}>
                        {Math.round(predictiveCompliance.reduce((sum, pc) => sum + pc.compliance_score, 0) / predictiveCompliance.length)}%
                      </Typography>
                    </Box>
                    <Assessment sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        High Priority Events
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {calendarEvents.filter(e => e.priority === 'high' || e.priority === 'critical').length}
                      </Typography>
                    </Box>
                    <Warning sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Documents Processed
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {documentProcessing.filter(dp => dp.processing_status === 'completed').length}
                      </Typography>
                    </Box>
                    <DocumentScanner sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        AI Predictions
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {predictiveCompliance.reduce((sum, pc) => sum + pc.predicted_issues.length, 0)}
                      </Typography>
                    </Box>
                    <AutoAwesome sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardHeader title="Compliance Score Trends" />
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={complianceScoreData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar 
                        dataKey="score" 
                        fill="#1976d2"
                        name="Compliance Score"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader title="Risk Distribution" />
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={riskDistributionData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {riskDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Predictive Compliance Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {predictiveCompliance.map((compliance, index) => (
            <Grid item xs={12} lg={6} key={compliance.fund_id}>
              <Card>
                <CardHeader
                  title={`Fund ${index + 1} Compliance Analysis`}
                  subheader={`Next Assessment: ${new Date(compliance.next_assessment_date).toLocaleDateString()}`}
                  action={
                    <Chip
                      label={`${compliance.compliance_score}% Compliant`}
                      color={compliance.compliance_score >= 80 ? 'success' : compliance.compliance_score >= 60 ? 'warning' : 'error'}
                    />
                  }
                />
                <CardContent>
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Compliance Score
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={compliance.compliance_score}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      {compliance.compliance_score}% ({compliance.risk_level} risk)
                    </Typography>
                  </Box>

                  {compliance.predicted_issues.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Predicted Issues
                      </Typography>
                      {compliance.predicted_issues.slice(0, 2).map((issue) => (
                        <Alert 
                          key={issue.id} 
                          severity={getSeverityColor(issue.severity) as any}
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {issue.title}
                          </Typography>
                          <Typography variant="caption">
                            Probability: {Math.round(issue.probability * 100)}%
                          </Typography>
                        </Alert>
                      ))}
                    </Box>
                  )}

                  {compliance.recommendations.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        AI Recommendations
                      </Typography>
                      {compliance.recommendations.slice(0, 1).map((rec) => (
                        <Card key={rec.id} variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {rec.title}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Timeline: {rec.implementation_timeline} | Cost: €{rec.implementation_cost.toLocaleString()}
                          </Typography>
                        </Card>
                      ))}
                    </Box>
                  )}

                  <Box mt={2}>
                    <Button
                      size="small"
                      onClick={() => setDetailDialog({ open: true, data: compliance, type: 'compliance' })}
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Document Analysis Tab */}
      {activeTab === 2 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">AI Document Processing</Typography>
            <Button
              variant="contained"
              startIcon={<DocumentScanner />}
              onClick={() => setAnalysisDialog({ open: true, document: null })}
            >
              Analyze New Document
            </Button>
          </Box>

          <Grid container spacing={3}>
            {documentProcessing.map((result, index) => (
              <Grid item xs={12} md={6} key={result.document_id}>
                <Card>
                  <CardHeader
                    title={`Document ${index + 1}`}
                    subheader={`Processing: ${result.processing_status}`}
                    action={
                      <Chip
                        label={`${Math.round(result.confidence_score * 100)}% Confidence`}
                        color={result.confidence_score > 0.8 ? 'success' : 'warning'}
                        size="small"
                      />
                    }
                  />
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      AI Summary:
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {result.ai_summary}
                    </Typography>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                      <Typography variant="caption" color="textSecondary">
                        Processed in {result.processing_time_ms}ms
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setDetailDialog({ open: true, data: result, type: 'document' })}
                      >
                        View Analysis
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Regulatory Calendar Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          {calendarEvents.map((event) => (
            <Grid item xs={12} md={6} key={event.id}>
              <Card>
                <CardHeader
                  title={event.title}
                  subheader={`${event.regulatory_body} • ${new Date(event.date).toLocaleDateString()}`}
                  action={
                    <Chip
                      label={event.priority}
                      color={getSeverityColor(event.priority) as any}
                      size="small"
                    />
                  }
                />
                <CardContent>
                  <Typography variant="body2" paragraph>
                    {event.description}
                  </Typography>

                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary">
                      Affected Regulations:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {event.affected_regulations.map((reg) => (
                        <Chip key={reg} label={reg} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary">
                      Fund Types:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {event.fund_types_affected.map((type) => (
                        <Chip key={type} label={type} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </Box>

                  {event.ai_generated && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        AI-Generated Event (Confidence: {Math.round((event.confidence_score || 0) * 100)}%)
                      </Typography>
                    </Alert>
                  )}

                  <Box mt={2}>
                    <Button
                      size="small"
                      onClick={() => setDetailDialog({ open: true, data: event, type: 'calendar' })}
                    >
                      View Recommendations
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Risk Analytics Tab */}
      {activeTab === 4 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Advanced risk analytics powered by AI will provide deep insights into regulatory compliance risks,
            stress testing scenarios, and predictive risk modeling.
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Risk Heat Map" />
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    Interactive risk visualization showing compliance risks across different regulatory areas.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Stress Testing" />
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    AI-powered stress testing scenarios for various regulatory compliance situations.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, data: null, type: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {detailDialog.type === 'compliance' && 'Compliance Analysis Details'}
          {detailDialog.type === 'document' && 'Document Analysis Results'}
          {detailDialog.type === 'calendar' && 'Regulatory Event Details'}
        </DialogTitle>
        <DialogContent>
          {detailDialog.data && detailDialog.type === 'compliance' && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Detailed Compliance Analysis
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Comprehensive compliance details would be displayed here with interactive charts and recommendations.
              </Typography>
            </Box>
          )}
          {detailDialog.data && detailDialog.type === 'document' && (
            <Box>
              <Typography variant="h6" gutterBottom>
                AI Document Analysis Results
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Detailed document analysis including extracted entities, key sections, and regulatory references.
              </Typography>
            </Box>
          )}
          {detailDialog.data && detailDialog.type === 'calendar' && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Event Preparation Recommendations
              </Typography>
              <Typography variant="body2" color="textSecondary">
                AI-generated preparation recommendations and timeline for this regulatory event.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, data: null, type: '' })}>
            Close
          </Button>
          <Button variant="contained" startIcon={<Download />}>
            Export Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analysis Dialog */}
      <Dialog
        open={analysisDialog.open}
        onClose={() => setAnalysisDialog({ open: false, document: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Analyze Regulatory Document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Document ID or Upload"
              placeholder="Enter document ID or upload file"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Analysis Type</InputLabel>
              <Select defaultValue="compliance_impact" label="Analysis Type">
                <MenuItem value="compliance_impact">Compliance Impact</MenuItem>
                <MenuItem value="risk_assessment">Risk Assessment</MenuItem>
                <MenuItem value="deadline_extraction">Deadline Extraction</MenuItem>
                <MenuItem value="regulatory_change">Regulatory Change</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info">
              The AI will analyze the document for regulatory implications, extract key dates,
              and provide compliance recommendations.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisDialog({ open: false, document: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={analyzing}
            onClick={() => handleAnalyzeDocument('sample-doc-id', 'compliance_impact')}
          >
            {analyzing ? <CircularProgress size={20} /> : 'Analyze Document'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegulatoryIntelligencePage;